import React, { useState, forwardRef } from 'react';
import {
  TableRow,
  TableCell,
  Typography,
  TextField,
  Autocomplete,
  IconButton,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PositionSelector from './PositionSelector';
import CurrencyInput from './../commons/CurrencyInput';
import { formatValue, toInputDate, toDisplayDate } from '../../utils/utils';

const EditableRow = React.memo(forwardRef(({ item, categories, onUpdate, onDelete, onError, selected = false, onSelect, highlighted = false }, ref) => {
  const [isEditing, setIsEditing] = useState({
    description: false,
    category: false,
    value: false,
    receivedDate: false,
    position: false,
  });
  const [description, setDescription] = useState(item.Description || '');
  const [selectedCategory, setSelectedCategory] = useState(item.Category || {});
  const [value, setValue] = useState(Number(item.Value) || 0);
  const [receivedDate, setReceivedDate] = useState(toInputDate(item.ReceivedDate));
  const [selectedPosition, setSelectedPosition] = useState(
    item.PositionId ? { id: item.PositionId, assetName: item.Ticker || '' } : null
  );
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleEditClick = (field) => {
    setIsEditing((prev) => ({ ...prev, [field]: true }));
  };

  const handleCancelEdit = (field) => {
    if (field === 'description') setDescription(item.Description || '');
    if (field === 'category') setSelectedCategory(item.Category || {});
    if (field === 'value') setValue(Number(item.Value) || 0);
    if (field === 'receivedDate') setReceivedDate(toInputDate(item.ReceivedDate));
    if (field === 'position') {
      setSelectedPosition(item.PositionId ? { id: item.PositionId, assetName: item.Ticker || '' } : null);
    }
    setIsEditing((prev) => ({ ...prev, [field]: false }));
  };

  const handleSave = async (field) => {
    const isDescriptionChanged = description !== item.Description;
    const isCategoryChanged = selectedCategory?.ID !== item.Category?.ID;
    const isValueChanged = value !== Number(item.Value);
    const isReceivedDateChanged = receivedDate !== toInputDate(item.ReceivedDate);
    const newPositionId = selectedPosition?.id || null;
    const oldPositionId = item.PositionId || null;
    const isPositionChanged = newPositionId !== oldPositionId;

    if (isDescriptionChanged || isCategoryChanged || isValueChanged || isReceivedDateChanged || isPositionChanged) {
      const formattedDate = toDisplayDate(receivedDate);
      const ticker = selectedPosition?.assetName || selectedPosition?.assetTicker || item.Ticker || null;
      await onUpdate(item.ID, description, selectedCategory?.ID, value, formattedDate, newPositionId, ticker);
    }

    setIsEditing((prev) => ({ ...prev, [field]: false }));
  };

  const handleConfirmDelete = () => {
    onDelete(item.ID);
    setDeleteOpen(false);
  };

  const handlePositionChange = (newValue) => {
    setSelectedPosition(newValue);
  };

  const handlePositionBlur = () => {
    handleSave('position');
  };

  return (
    <TableRow
      ref={ref}
      hover
      sx={highlighted ? {
        bgcolor: 'action.selected',
        animation: 'highlight-fade 3s ease-out',
        '@keyframes highlight-fade': {
          '0%': { bgcolor: 'primary.light' },
          '100%': { bgcolor: 'transparent' },
        },
      } : {}}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={selected}
          onChange={onSelect}
          size="small"
        />
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>
        {isEditing.description ? (
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => handleSave('description')}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCancelEdit('description');
              if (e.key === 'Enter') handleSave('description');
            }}
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
          />
        ) : (
          <Typography
            onClick={() => handleEditClick('description')}
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          >
            {description || '-'}
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ minWidth: 120 }}>
        {isEditing.category ? (
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option?.Name || ''}
            value={selectedCategory}
            onChange={(event, newValue) => setSelectedCategory(newValue)}
            onBlur={() => handleSave('category')}
            isOptionEqualToValue={(option, val) => option?.ID === val?.ID}
            renderInput={(params) => (
              <TextField {...params} autoFocus fullWidth size="small" variant="outlined" />
            )}
            size="small"
          />
        ) : (
          <Typography
            onClick={() => handleEditClick('category')}
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          >
            {selectedCategory?.Name || '-'}
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ minWidth: 150 }}>
        {isEditing.position ? (
          <Box sx={{ minWidth: 140 }}>
            <PositionSelector
              value={selectedPosition}
              onChange={handlePositionChange}
              onBlur={handlePositionBlur}
              onError={onError}
              disabled={false}
              compact
            />
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
            onClick={() => handleEditClick('position')}
          >
            {item.Ticker ? (
              <Chip label={item.Ticker} size="small" color="primary" variant="outlined" />
            ) : (
              <Typography variant="body2" color="text.secondary">-</Typography>
            )}
            <IconButton size="small" sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </TableCell>

      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        <Chip label={item.Currency} size="small" variant="outlined" />
      </TableCell>

      <TableCell sx={{ minWidth: 100 }}>
        {isEditing.value ? (
          <CurrencyInput
            value={value}
            onChange={setValue}
            onBlur={() => handleSave('value')}
            currency={item.Currency}
            label=""
            size="small"
            variant="outlined"
            autoFocus
          />
        ) : (
          <Typography
            onClick={() => handleEditClick('value')}
            sx={{
              cursor: 'pointer',
              fontWeight: 500,
              color: 'success.main',
              '&:hover': { color: 'success.dark' },
            }}
          >
            {formatValue(value, item.Currency)}
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ minWidth: 120, display: { xs: 'none', sm: 'table-cell' } }}>
        {isEditing.receivedDate ? (
          <TextField
            type="date"
            value={receivedDate}
            onChange={(e) => setReceivedDate(e.target.value)}
            onBlur={() => handleSave('receivedDate')}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCancelEdit('receivedDate');
            }}
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
          />
        ) : (
          <Typography
            onClick={() => handleEditClick('receivedDate')}
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          >
            {toDisplayDate(receivedDate)}
          </Typography>
        )}
      </TableCell>

      <TableCell>
        <IconButton onClick={() => setDeleteOpen(true)} color="error" size="small">
          <DeleteIcon fontSize="small" />
        </IconButton>
        <Dialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Confirmar Exclusao</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Tem certeza de que deseja excluir este lancamento?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)} color="primary">
              Cancelar
            </Button>
            <Button onClick={handleConfirmDelete} color="error" autoFocus>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </TableCell>
    </TableRow>
  );
}));

export default EditableRow;
